/**
 * Transport Settings — Admin (Phase 2)
 *
 * Reads/writes /api/transport/settings (RBAC: transport.configure).
 */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function TransportSettingsAdminPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [featureDisabled, setFeatureDisabled] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/transport/settings').catch(e => e.response);
      if (r?.status === 403 && r?.data?.error?.code === 'FEATURE_DISABLED') {
        setFeatureDisabled(true);
      } else {
        setSettings(r?.data?.data || {});
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ndis_rate_per_km: Number(settings.ndis_rate_per_km),
        staff_reimbursement_rate_per_km: Number(settings.staff_reimbursement_rate_per_km),
        approval_threshold_km: Number(settings.approval_threshold_km),
        require_odometer_photos: !!settings.require_odometer_photos,
        ndis_support_item: settings.ndis_support_item || '',
      };
      await api.put('/transport/settings', payload);
      toast.success('Settings saved');
      await load();
    } catch (err) {
      toast.error('Failed to save', { description: err?.response?.data?.error?.message });
    } finally { setSaving(false); }
  };

  if (featureDisabled) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <h1 className="text-2xl font-semibold">Transport · Settings</h1>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Vehicle Log module is disabled. Enable <code>transport_logging_enabled</code> in Feature Flags.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading || !settings) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground"><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading…</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-4" data-testid="transport-settings-admin-page">
      <div>
        <h1 className="text-2xl font-semibold">Transport · Settings</h1>
        <p className="text-sm text-muted-foreground">Org-wide rates, thresholds, and photo policy for the Vehicle Log module.</p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Rates & policies</CardTitle>
          <CardDescription>Per-vehicle overrides registered on a staff vehicle take precedence over these org defaults.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>NDIS rate ($ / km)</Label>
              <Input type="number" step="0.01" value={settings.ndis_rate_per_km}
                     onChange={e => setSettings({ ...settings, ndis_rate_per_km: e.target.value })}
                     data-testid="settings-ndis-rate" />
            </div>
            <div>
              <Label>Staff reimbursement rate ($ / km)</Label>
              <Input type="number" step="0.01" value={settings.staff_reimbursement_rate_per_km}
                     onChange={e => setSettings({ ...settings, staff_reimbursement_rate_per_km: e.target.value })}
                     data-testid="settings-reimb-rate" />
            </div>
            <div>
              <Label>Auto-approve threshold (km)</Label>
              <Input type="number" value={settings.approval_threshold_km}
                     onChange={e => setSettings({ ...settings, approval_threshold_km: e.target.value })}
                     data-testid="settings-threshold-km" />
              <p className="text-xs text-muted-foreground mt-1">Only active when <code>transport_auto_approve_under_threshold</code> flag is ON.</p>
            </div>
            <div>
              <Label>NDIS support item code</Label>
              <Input value={settings.ndis_support_item || ''}
                     onChange={e => setSettings({ ...settings, ndis_support_item: e.target.value })}
                     placeholder="02_051_0107_1_1"
                     data-testid="settings-support-item" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input id="require_odo_photo" type="checkbox"
                   checked={!!settings.require_odometer_photos}
                   onChange={e => setSettings({ ...settings, require_odometer_photos: e.target.checked })}
                   data-testid="settings-require-photo" />
            <Label htmlFor="require_odo_photo">Require odometer photo on every trip log</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Note: Photo enforcement only kicks in when the <code>transport_odometer_photo_required</code> feature flag is also ON.
          </p>
          <Button onClick={save} disabled={saving} data-testid="settings-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save changes
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
